import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function SignupPage() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "",
    handle: "",
    displayName: "",
    password: "",
    isCreator: false,
  });
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await signup({
        email: form.email,
        handle: form.handle,
        displayName: form.displayName || undefined,
        password: form.password,
        isCreator: form.isCreator,
      });
      nav(form.isCreator ? "/studio" : "/dashboard");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Signup failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-md py-10">
      <Card>
        <CardHeader>
          <CardTitle>Create your account</CardTitle>
          <CardDescription>
            Your handle is the URL people use to find you — fanfix.me/@your-handle.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required autoComplete="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="handle">Handle</Label>
                <Input id="handle" required minLength={3} maxLength={32} placeholder="yourhandle" value={form.handle} onChange={e => setForm({ ...form, handle: e.target.value.toLowerCase() })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="displayName">Display name</Label>
                <Input id="displayName" value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} placeholder="Alex Rivera" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                <p className="text-xs text-muted-foreground">At least 8 characters.</p>
              </div>
              <label className="col-span-2 flex items-start gap-2 rounded-md border p-3 text-sm">
                <Checkbox checked={form.isCreator} onCheckedChange={v => setForm({ ...form, isCreator: !!v })} />
                <span className="space-y-1">
                  <span className="block font-medium">I'm a creator and want to accept commissions.</span>
                  <span className="block text-xs text-muted-foreground">
                    You can flip this in Settings later.
                  </span>
                </span>
              </label>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Creating account…" : "Create account"}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="underline underline-offset-4">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
